import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import toast from "react-hot-toast";
import {
    Box, Flex, Stack, Text, Button, Badge, Spinner, Select, Icon, useColorModeValue,
} from "@chakra-ui/react";
import { MdLayers } from "react-icons/md";
import { fetchLegalVersions, fetchLegalVersion } from "./legalVersionsApi";

/**
 * Loads the immutable snapshots list into state.
 * @param {number} docId - Legal document id
 * @param {Function} setList - Setter for the snapshots list
 * @param {Function} setLoading - Setter for the loading flag
 * @returns {Promise<void>}
 */
async function loadVersions(docId, setList, setLoading) {
    setLoading(true);
    try {
        setList(await fetchLegalVersions(docId));
    } catch (error) {
        toast.error("Chargement des versions immuables impossible");
        setList([]);
    } finally {
        setLoading(false);
    }
}

/**
 * Fetches both selected versions and stores their content for diffing.
 * @param {number} docId - Legal document id
 * @param {string} verA - First version label
 * @param {string} verB - Second version label
 * @param {Function} setPair - Setter for { a, b } contents
 * @returns {Promise<void>}
 */
async function loadDiffPair(docId, verA, verB, setPair) {
    if (!verA || !verB) return;
    try {
        const [a, b] = await Promise.all([fetchLegalVersion(docId, verA), fetchLegalVersion(docId, verB)]);
        setPair({ a: a?.content, b: b?.content });
    } catch (error) {
        toast.error("Comparaison des versions impossible");
    }
}

/**
 * Maps a content object to a section map keyed by section number.
 * @param {object} content - The snapshot content { sections: [...] }
 * @returns {object} Map of key -> "title\ntext"
 */
function sectionsMap(content) {
    const map = {};
    (content?.sections || []).forEach((s, i) => {
        map[s.number ?? i + 1] = `${s.title || ""}\n${s.text || ""}`.trim();
    });
    return map;
}

/**
 * Builds a per-section diff between two contents.
 * @param {object} contentA - First version content
 * @param {object} contentB - Second version content
 * @returns {Array} Rows { key, aText, bText, changed }
 */
function buildSectionDiff(contentA, contentB) {
    const a = sectionsMap(contentA);
    const b = sectionsMap(contentB);
    const keys = Array.from(new Set([...Object.keys(a), ...Object.keys(b)]));
    return keys.map((key) => ({ key, aText: a[key] || "—", bText: b[key] || "—", changed: (a[key] || "") !== (b[key] || "") }));
}

/**
 * Renders one snapshot metadata row.
 * @param {object} props - { snap }
 * @returns {React.ReactElement}
 */
function SnapshotMetaRow({ snap }) {
    const titleColor = useColorModeValue("secondaryGray.900", "whiteAlpha.900");
    const metaColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
    return (
        <Flex justify="space-between" gap="8px" py="6px" borderBottom="1px solid" borderColor="whiteAlpha.200">
            <Text fontSize="0.78rem" color={titleColor}>
                v{snap.version} {snap.was_active && (
                    <Badge ml="4px" bg="rgba(76,175,80,0.2)" color="#81c784" borderRadius="full"
                        fontSize="0.6rem" textTransform="none">active</Badge>)}
            </Text>
            <Text fontSize="0.72rem" textAlign="right" color={metaColor}>
                effet {snap.effective_date ? dayjs(snap.effective_date).format("DD/MM/YYYY") : "—"} · admin #{snap.edited_by_admin_id ?? "?"} · {dayjs(snap.created_at).format("DD/MM/YYYY")}
            </Text>
        </Flex>
    );
}

/**
 * Renders one diff row for a section (highlighted when changed).
 * @param {object} props - { row }
 * @returns {React.ReactElement}
 */
function DiffRow({ row }) {
    const cellColor = useColorModeValue("secondaryGray.800", "whiteAlpha.900");
    const cellProps = { flex: 1, p: "8px", fontSize: "0.72rem", whiteSpace: "pre-wrap", color: cellColor, fontFamily: "mono" };
    return (
        <Flex gap="8px" mb="4px" border="1px solid" borderRadius="8px"
            borderColor={row.changed ? "rgba(255,152,0,0.4)" : "whiteAlpha.200"}
            bg={row.changed ? "rgba(255,152,0,0.06)" : "transparent"}>
            <Text {...cellProps}>{row.aText}</Text>
            <Text {...cellProps} borderLeft="1px solid" borderColor="whiteAlpha.200">{row.bText}</Text>
        </Flex>
    );
}

/**
 * A single version dropdown selector.
 * @param {object} props - { label, value, onChange, list }
 * @returns {React.ReactElement}
 */
function VersionSelect({ label, value, onChange, list }) {
    return (
        <Select size="sm" borderRadius="10px" minW="140px" aria-label={label} value={value}
            onChange={(e) => onChange(e.target.value)}>
            {list.map((s) => <option key={s.version} value={s.version}>v{s.version}</option>)}
        </Select>
    );
}

/**
 * Renders the two version selectors and the resulting section diff.
 * @param {object} props - { list, docId }
 * @returns {React.ReactElement}
 */
function DiffPanel({ list, docId }) {
    const [verA, setVerA] = useState("");
    const [verB, setVerB] = useState("");
    const [pair, setPair] = useState({ a: null, b: null });

    useEffect(() => {
        if (list.length >= 2) { setVerA(list[1].version); setVerB(list[0].version); }
    }, [list]);
    useEffect(() => { loadDiffPair(docId, verA, verB, setPair); }, [docId, verA, verB]);

    const rows = buildSectionDiff(pair.a, pair.b);
    return (
        <Box mt="16px">
            <Stack direction="row" spacing="16px" mb="12px">
                <VersionSelect label="Version A" value={verA} onChange={setVerA} list={list} />
                <VersionSelect label="Version B" value={verB} onChange={setVerB} list={list} />
            </Stack>
            {rows.map((row) => <DiffRow key={row.key} row={row} />)}
        </Box>
    );
}

/**
 * Renders the snapshots list and diff, or an empty state.
 * @param {object} props - { list, docId }
 * @returns {React.ReactElement}
 */
function SnapshotsBody({ list, docId }) {
    const emptyColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
    if (!list.length) {
        return <Text fontSize="0.8rem" mt="8px" color={emptyColor}>Aucun snapshot pour ce document.</Text>;
    }
    return (
        <>
            <Box mt="8px">{list.map((snap) => <SnapshotMetaRow key={snap.id || snap.version} snap={snap} />)}</Box>
            {list.length >= 2 && <DiffPanel list={list} docId={docId} />}
        </>
    );
}

/**
 * Self-contained panel exposing the immutable legal-document version snapshots
 * with a two-version diff. Collapsed by default; loads on first expand.
 * @param {object} props - { docId }
 * @returns {React.ReactElement}
 */
function LegalVersionsSnapshots({ docId }) {
    const [open, setOpen] = useState(false);
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(false);
    const panelBg = useColorModeValue("secondaryGray.100", "whiteAlpha.100");
    const labelColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");

    useEffect(() => { setOpen(false); setList([]); }, [docId]);
    useEffect(() => { if (open && docId) loadVersions(docId, setList, setLoading); }, [open, docId]);

    return (
        <Box mb="16px">
            <Button size="sm" variant="ghost" leftIcon={<Icon as={MdLayers} />}
                color={open ? "brand.500" : "secondaryGray.500"} mb="8px" onClick={() => setOpen(!open)}>
                Versions immuables {open ? "▲" : "▼"}
            </Button>
            {open && (
                <Box p="16px" borderRadius="14px" bg={panelBg}>
                    <Text fontSize="0.8rem" fontWeight="600" textTransform="uppercase" letterSpacing="0.5px" color={labelColor}>
                        Snapshots figés (preuve RGPD du contenu en vigueur)
                    </Text>
                    {loading
                        ? <Flex justify="center" p="16px"><Spinner size="sm" color="brand.500" /></Flex>
                        : <SnapshotsBody list={list} docId={docId} />}
                </Box>
            )}
        </Box>
    );
}

export default LegalVersionsSnapshots;
